import React, { useState, useCallback, useEffect, useRef } from 'react';
import { formatVacationDate } from 'utils/vacation';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { ProfileAnimatedPoints } from 'components/Points/ProfileAnimatedPoints';
import MagicButton from 'components/MagicButton/MagicButton';
import { PublicUserProfile } from 'types/interfaces/profile';
import { Skeleton } from '@mui/material';
import { AssetUrls } from 'config/assets';
import arevakhachIcon from 'assets/icons/arevakhach-icon.svg';
import { toast } from 'react-toastify';
import { SocialLinksSection } from '../SocialLinksSection/SocialLinksSection';
import { linkifyText } from 'utils/urlDetection';
import EliteBadge from 'components/EliteBadge/EliteBadge';
// import { FollowButton } from '../FollowButton/FollowButton';
// import { useGetFollowStatusQuery } from 'store/services/social/followApi';
import './ProfileCard.css';
import { copyToClipboard } from 'utils/copyToClipboard';

interface ProfileCardProps {
  profile: PublicUserProfile | undefined;
  isLoading: boolean;
  isOwnProfile: boolean;
  showContactButton: boolean;
  onContactClick: () => void;
  onSendPointsClick: () => void;
  onSendCardsClick: () => void;
  isViewerElite?: boolean;
  viewerHasBlocked?: boolean;
  hideActionButtons?: boolean;
  onBlockClick?: () => void;
  onUnblockClick?: () => void;
}

// Format location string based on country
const formatLocation = (city: string | null, state: string | null, country: string | null): string | null => {
  const isUS = country?.toUpperCase() === 'USA' || country?.toUpperCase() === 'US';

  if (isUS) {
    // US: "City, State"
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return null;
  } else {
    // International: "City, Country"
    if (city && country) return `${city}, ${country}`;
    if (city) return city;
    if (country) return country;
    return null;
  }
};

// Format join date as "Trading since Month Year"
const formatJoinDate = (createdAt: string): string => {
  const date = new Date(createdAt);
  const month = date.toLocaleString('en-US', { month: 'long' });
  const year = date.getFullYear();
  return `Trading since ${month} ${year}`;
};

// Render star rating
const StarRating: React.FC<{ rating: number | null; count: number }> = ({ rating, count }) => {
  const displayRating = rating ?? 0;
  const fullStars = Math.floor(displayRating);
  const hasHalfStar = displayRating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="profile-card-rating">
      <div className="profile-card-stars">
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} className="star full">&#9733;</span>
        ))}
        {hasHalfStar && <span className="star half">&#9733;</span>}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} className="star empty">&#9734;</span>
        ))}
      </div>
      <span className="profile-card-rating-count">({count} ratings)</span>
    </div>
  );
};

const ProfileMoreMenu: React.FC<{
  profile: PublicUserProfile;
  isOwnProfile: boolean;
  viewerHasBlocked?: boolean;
  onBlockClick?: () => void;
  onUnblockClick?: () => void;
}> = ({ profile, isOwnProfile, viewerHasBlocked, onBlockClick, onUnblockClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleCopyLink = useCallback(async () => {
    setIsOpen(false);
    const url = profile.customSlug
      ? `https://trademagic.gg/u/${profile.customSlug}`
      : `https://trademagic.gg/profile/u/${profile.username}`;
    try {
      if (await copyToClipboard(url)) toast.success('Profile link copied!');
      else toast.error('Failed to copy link');
    } catch {
      toast.error('Failed to copy link');
    }
  }, [profile.customSlug, profile.username]);

  const handleBlockItemClick = () => {
    setIsOpen(false);
    if (viewerHasBlocked) {
      onUnblockClick?.();
    } else {
      onBlockClick?.();
    }
  };

  return (
    <div className="profile-card-more-menu" ref={containerRef}>
      <button
        className="profile-card-share-btn"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
        title="More options"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="3" cy="8" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="13" cy="8" r="1.5" />
        </svg>
      </button>
      {isOpen && (
        <div className="profile-card-more-menu-list" role="menu">
          <button
            className="profile-card-more-menu-item"
            role="menuitem"
            onClick={handleCopyLink}
          >
            Copy profile link
          </button>
          {!isOwnProfile && (
            <button
              className={`profile-card-more-menu-item ${viewerHasBlocked ? '' : 'profile-card-more-menu-item--danger'}`}
              role="menuitem"
              onClick={handleBlockItemClick}
            >
              {viewerHasBlocked ? 'Unblock' : 'Block user'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Hidden while the Friend feature is paused for production.
// const FollowCounts: React.FC<{ userId: number }> = ({ userId }) => {
//   const { data } = useGetFollowStatusQuery(userId, { skip: !userId });
//   if (!data) return null;
//   return (
//     <div className="profile-card-follow-counts">
//       <span>
//         <strong>{data.followerCount}</strong>{' '}
//         {data.followerCount === 1 ? 'follower' : 'followers'}
//       </span>
//       <span className="profile-card-follow-counts-dot">·</span>
//       <span>
//         <strong>{data.followingCount}</strong> following
//       </span>
//     </div>
//   );
// };

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  isLoading,
  isOwnProfile,
  showContactButton,
  onContactClick,
  onSendPointsClick,
  onSendCardsClick,
  isViewerElite = false,
  viewerHasBlocked = false,
  hideActionButtons = false,
  onBlockClick,
  onUnblockClick,
}) => {
  if (isLoading) {
    return (
      <div className="profile-card profile-card--loading">
        <Skeleton variant="circular" width={120} height={120} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width={150} height={32} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 2 }} />
        <Skeleton variant="text" width={100} height={24} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="text" width={80} height={24} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="text" width={120} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="text" width={160} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 2, borderRadius: 2 }} />
        <div className="profile-card-buttons">
          <Skeleton variant="rectangular" width="48%" height={44} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
          <Skeleton variant="rectangular" width="48%" height={44} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const location = formatLocation(profile.city, profile.state, profile.country);

  return (
    <div className="profile-card">
      <div className="profile-card-avatar-section">
        <AvatarWithFlag
          avatarUrl={profile.avatarUrl}
          username={profile.username}
          countryCode={profile.country}
          size={120}
          isElite={profile.isElite}
          showAnimatedBorder={profile.showAnimatedBorder}
          borderColor1={profile.borderColor1}
          borderColor2={profile.borderColor2}
        />
        <ProfileAnimatedPoints points={profile.points} playSound={false} />
      </div>

      <div className="profile-card-username-row">
        <h1 className="profile-card-username">{profile.username}<EliteBadge isElite={!!profile.isElite} /></h1>
        {profile.vacation?.active && (
          <span className="profile-card-away-pill" data-testid="profile-away-pill">
            {profile.vacation.until
              ? `Away until ${formatVacationDate(profile.vacation.until, { weekday: false })}`
              : 'Away'}
          </span>
        )}
        <ProfileMoreMenu
          profile={profile}
          isOwnProfile={isOwnProfile}
          viewerHasBlocked={viewerHasBlocked}
          onBlockClick={onBlockClick}
          onUnblockClick={onUnblockClick}
        />
      </div>

      {/* <FollowCounts userId={profile.id} /> */}

      <StarRating rating={profile.averageRating} count={profile.totalRatings} />

      {location && (
        <div className="profile-card-location">
          <span className="profile-card-location-pin">📍</span>
          <span>{location}</span>
        </div>
      )}

      <div className="profile-card-join-date">
        {formatJoinDate(profile.createdAt)}
      </div>

      {profile.bio && (
        <div className="profile-card-bio">
          {profile.isElite ? linkifyText(profile.bio) : profile.bio}
        </div>
      )}

      {!isOwnProfile && !hideActionButtons && (
        <div className="profile-card-buttons">
          <div className="profile-card-buttons-row">
            {/* <FollowButton userId={profile.id} /> */}
            {showContactButton && (
              <MagicButton
                label="Contact"
                color="dark-grey"
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/contact.svg`}
                    alt="Contact"
                    style={{ width: 16, height: 16 }}
                  />
                }
                onPress={onContactClick}
              />
            )}
            <MagicButton
              label="Send Points"
              color="dark-grey"
              icon={
                <img
                  src={AssetUrls.icons.profile.coin}
                  alt="Points"
                  style={{ width: 16, height: 16 }}
                />
              }
              onPress={onSendPointsClick}
            />
          </div>
          <MagicButton
            label="Send Cards"
            color="purple"
            icon={
              <img
                src={arevakhachIcon}
                alt="Send"
                style={{ width: 16, height: 16 }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            }
            onPress={onSendCardsClick}
          />
        </div>
      )}

      <SocialLinksSection
        username={profile.username}
        isOwnProfile={isOwnProfile}
        isViewerElite={isViewerElite}
      />
    </div>
  );
};

export default ProfileCard;
